// Fleet data: sites and appliances (REST) plus the live socket (/v1/ws):
// presence, endpoint state, and in-band actions.
import type { ActionOutcome, ActionRequest, EndpointUpdate } from './controlApi';

export interface Site {
  id: string;
  name: string;
  timezone: string;
  /** Needed for sunrise/sunset schedules; null when not set. */
  latitude?: number | null;
  longitude?: number | null;
}

export interface Appliance {
  id: string;
  serial: string;
  site_id: string | null;
  name: string | null;
  status: 'online' | 'offline';
  last_seen_at: string | null;
  agent_version: string | null;
  platform: string | null;
}

/** An RFC 9457 problem from the API, with its stable code. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    detail: string,
  ) {
    super(detail);
    this.name = 'ApiError';
  }
}

function problem(res: Response, body: unknown): ApiError {
  const p = (body ?? {}) as { code?: unknown; detail?: unknown };
  return new ApiError(
    res.status,
    typeof p.code === 'string' ? p.code : 'http_error',
    typeof p.detail === 'string' ? p.detail : `API responded with HTTP ${String(res.status)}`,
  );
}

/** Calls the API with the bearer token and checks the JSON answer. */
export async function call<T>(
  url: string,
  token: string,
  init: RequestInit,
  check: (v: unknown) => v is T,
  fetchImpl: typeof fetch = fetch,
): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set('Authorization', `Bearer ${token}`);
  const res = await fetchImpl(url, { ...init, headers });
  const body: unknown = await res.json().catch(() => null);
  if (!res.ok) throw problem(res, body);
  if (!check(body)) throw new Error('API returned an unexpected payload');
  return body;
}

/** Calls the API for an empty (204) answer. */
export async function callNoContent(url: string, token: string, init: RequestInit): Promise<void> {
  const headers = new Headers(init.headers);
  headers.set('Authorization', `Bearer ${token}`);
  const res = await fetch(url, { ...init, headers });
  if (!res.ok) throw problem(res, await res.json().catch(() => null));
}

const hasArray =
  <K extends string>(key: K) =>
  (v: unknown): v is Record<K, unknown[]> =>
    typeof v === 'object' && v !== null && Array.isArray((v as Record<string, unknown>)[key]);

function isAppliance(v: unknown): v is Appliance {
  if (typeof v !== 'object' || v === null) return false;
  const o = v as Record<string, unknown>;
  return typeof o.id === 'string' && typeof o.serial === 'string' && typeof o.status === 'string';
}

export async function fetchSites(
  apiUrl: string,
  token: string,
  tenantId: string,
  signal?: AbortSignal,
  fetchImpl: typeof fetch = fetch,
): Promise<Site[]> {
  const body = await call(
    `${apiUrl}/v1/tenants/${encodeURIComponent(tenantId)}/sites`,
    token,
    { signal },
    hasArray('sites'),
    fetchImpl,
  );
  return body.sites as Site[];
}

export async function fetchAppliances(
  apiUrl: string,
  token: string,
  tenantId: string,
  signal?: AbortSignal,
  fetchImpl: typeof fetch = fetch,
): Promise<Appliance[]> {
  const body = await call(
    `${apiUrl}/v1/tenants/${encodeURIComponent(tenantId)}/appliances`,
    token,
    { signal },
    hasArray('appliances'),
    fetchImpl,
  );
  return body.appliances.filter(isAppliance);
}

export async function claimAppliance(
  apiUrl: string,
  token: string,
  tenantId: string,
  claim: { claim_code: string; site_id: string; name: string },
  fetchImpl: typeof fetch = fetch,
): Promise<Appliance> {
  return call(
    `${apiUrl}/v1/tenants/${encodeURIComponent(tenantId)}/appliances/claim`,
    token,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(claim),
    },
    isAppliance,
    fetchImpl,
  );
}

/** A one-time provisioning token and what the unit needs with it (ADR-0022). */
export interface ProvisioningToken {
  id: string;
  /** Shown once; never stored by the app. */
  token: string;
  serial: string;
  name: string;
  site_id: string;
  site_name: string;
  expires_at: string;
  /** The API's public address, for the commands the unit runs. */
  api_url: string;
}

function isProvisioningToken(v: unknown): v is ProvisioningToken {
  if (typeof v !== 'object' || v === null) return false;
  const o = v as Record<string, unknown>;
  return (
    typeof o.token === 'string' &&
    typeof o.serial === 'string' &&
    typeof o.api_url === 'string' &&
    typeof o.expires_at === 'string'
  );
}

export async function createProvisioningToken(
  apiUrl: string,
  token: string,
  tenantId: string,
  siteId: string,
  unit: { serial: string; name: string },
  fetchImpl: typeof fetch = fetch,
): Promise<ProvisioningToken> {
  return call(
    `${apiUrl}/v1/tenants/${encodeURIComponent(tenantId)}/sites/${encodeURIComponent(siteId)}/provisioning-tokens`,
    token,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(unit),
    },
    isProvisioningToken,
    fetchImpl,
  );
}

/** Quotes a value for a POSIX shell. */
function shellQuote(s: string): string {
  return /^[A-Za-z0-9_./:=@+-]+$/.test(s) ? s : `'${s.replace(/'/g, `'\\''`)}'`;
}

/**
 * The commands that set up a unit with a provisioning token, on Debian or
 * Ubuntu (WSL included): download the agent package with the token, install
 * it, provision, and start the agent.
 */
export function setupCommands(t: ProvisioningToken): string {
  const api = t.api_url.replace(/\/+$/, '');
  const allowHttp = api.startsWith('http://') ? ' --allow-http' : '';
  return [
    `TOKEN=${shellQuote(t.token)}`,
    `curl -fsSL -H "Authorization: Bearer $TOKEN" -o illumilink-agent.deb \\`,
    `  "${api}/v1/provisioning/agent-package?arch=$(dpkg --print-architecture)"`,
    `sudo apt-get install -y ./illumilink-agent.deb`,
    `sudo -u illumilink illumilink-agent provision --api ${shellQuote(api)}${allowHttp} \\`,
    `  --serial ${shellQuote(t.serial)} --token "$TOKEN"`,
    `sudo systemctl enable --now illumilink-agent`,
  ].join('\n');
}

/** ws(s):// URL of the live socket for an http(s):// API URL. */
export function liveUrl(apiUrl: string): string {
  const u = new URL(apiUrl);
  u.protocol = u.protocol === 'https:' ? 'wss:' : 'ws:';
  u.pathname = `${u.pathname.replace(/\/+$/, '')}/v1/ws`;
  return u.toString();
}

export type LiveState = 'connecting' | 'live' | 'reconnecting';

export interface LiveOptions {
  url: string;
  tenantId: string;
  /** Narrows endpoint events to one site. */
  siteId?: string;
  getToken: () => string | undefined;
  onAppliance: (a: Appliance & { tenant_id: string }) => void;
  onEndpoint?: (u: EndpointUpdate) => void;
  onState: (s: LiveState) => void;
  /** Called after a (re)connect so the caller can refetch what it missed. */
  onResync?: () => void;
  socketFactory?: (url: string) => WebSocket;
}

/** How long an in-band action may take before the UI gives up waiting. */
export const ACTION_TIMEOUT_MS = 12_000;

interface Pending {
  resolve: (o: ActionOutcome) => void;
  reject: (e: ApiError) => void;
  timer: ReturnType<typeof setTimeout>;
}

/**
 * Keeps one live socket open: authenticates with the access token as the
 * first message (never in the URL), subscribes to the tenant, and
 * reconnects with backoff. Call refreshToken() when the token renews.
 * Actions sent with action() travel on the same socket (ADR-0009).
 */
export class LiveClient {
  private ws: WebSocket | undefined;
  private stopped = false;
  private attempt = 0;
  private timer: ReturnType<typeof setTimeout> | undefined;
  private connectedOnce = false;
  private live = false;
  private seq = 0;
  private readonly pending = new Map<string, Pending>();

  constructor(private readonly opts: LiveOptions) {
    this.connect();
  }

  /** Whether the socket is subscribed and can carry actions. */
  get isLive() {
    return this.live && this.ws?.readyState === WebSocket.OPEN;
  }

  /**
   * Sends an action on the socket and resolves with its outcome, or rejects
   * with an ApiError. Returns undefined when the socket isn't live (use the
   * REST API instead).
   */
  action(req: ActionRequest & { site_id: string }): Promise<ActionOutcome> | undefined {
    const ws = this.ws;
    if (!this.isLive || !ws) return undefined;
    this.seq++;
    const ref = `a${String(this.seq)}`;
    return new Promise<ActionOutcome>((resolve, reject) => {
      const timer = setTimeout(() => {
        this.pending.delete(ref);
        reject(new ApiError(0, 'timeout', 'No answer from the appliance in time.'));
      }, ACTION_TIMEOUT_MS);
      this.pending.set(ref, { resolve, reject, timer });
      ws.send(JSON.stringify({ type: 'action', ref, ...req }));
    });
  }

  private failPending(code: string, detail: string) {
    for (const [ref, p] of this.pending) {
      clearTimeout(p.timer);
      p.reject(new ApiError(0, code, detail));
      this.pending.delete(ref);
    }
  }

  private connect() {
    if (this.stopped) return;
    this.opts.onState(this.connectedOnce ? 'reconnecting' : 'connecting');
    const make = this.opts.socketFactory ?? ((u: string) => new WebSocket(u));
    const ws = make(this.opts.url);
    this.ws = ws;
    ws.onopen = () => {
      const token = this.opts.getToken();
      if (!token) {
        ws.close();
        return;
      }
      ws.send(JSON.stringify({ type: 'auth', token }));
      const sub: Record<string, string> = { type: 'subscribe', tenant_id: this.opts.tenantId };
      if (this.opts.siteId) sub.site_id = this.opts.siteId;
      ws.send(JSON.stringify(sub));
    };
    ws.onmessage = (ev: MessageEvent<string>) => {
      let msg: {
        type?: string;
        appliance?: unknown;
        endpoint?: EndpointUpdate;
        ref?: string;
        status?: string;
        result?: ActionOutcome;
        code?: string;
        detail?: string;
      };
      try {
        msg = JSON.parse(ev.data) as typeof msg;
      } catch {
        return;
      }
      if (msg.type === 'subscribed') {
        this.attempt = 0;
        this.live = true;
        const resync = this.connectedOnce;
        this.connectedOnce = true;
        this.opts.onState('live');
        if (resync) this.opts.onResync?.();
      } else if (msg.type === 'appliance' && isAppliance(msg.appliance)) {
        this.opts.onAppliance(msg.appliance as Appliance & { tenant_id: string });
      } else if (msg.type === 'endpoint' && msg.endpoint) {
        this.opts.onEndpoint?.(msg.endpoint);
      } else if (msg.type === 'action_result' && msg.ref) {
        const p = this.pending.get(msg.ref);
        if (!p) return;
        this.pending.delete(msg.ref);
        clearTimeout(p.timer);
        if (msg.status === 'ok' && msg.result) p.resolve(msg.result);
        else
          p.reject(
            new ApiError(0, msg.code ?? 'action_failed', msg.detail ?? 'The action failed.'),
          );
      }
    };
    ws.onclose = () => {
      if (this.ws !== ws || this.stopped) return;
      this.live = false;
      this.failPending('connection_lost', 'The connection dropped before the appliance answered.');
      this.opts.onState('reconnecting');
      const delay = Math.min(30_000, 500 * 2 ** this.attempt) * (0.5 + Math.random() / 2);
      this.attempt++;
      this.timer = setTimeout(() => {
        this.connect();
      }, delay);
    };
  }

  /** Re-authenticate the open socket with a renewed token. */
  refreshToken() {
    const token = this.opts.getToken();
    if (token && this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type: 'auth', token }));
    }
  }

  close() {
    this.stopped = true;
    this.live = false;
    clearTimeout(this.timer);
    this.failPending('connection_lost', 'The page closed the connection.');
    this.ws?.close();
  }
}
